import { LangEn } from 'assets/locales/en';
import { LangFr } from 'assets/locales/fr';
import { fromIsoDay } from 'helper/timeLabels';

/**
 * The repetition of an appointment, as the api takes it (contract § A):
 * { freq: 'daily' | 'weekly' | 'monthly' | 'yearly', interval: 1 to 99,
 *   byWeekday: ['MO', …] (weekly only), monthlyBy: 'monthday' | 'weekday'
 *   (monthly only), end: { type: 'never' } | { type: 'until', day } |
 *   { type: 'count', count } }.
 * A monthly 'weekday' rule repeats the n-th weekday of the first day,
 * n = ceil(date / 7), a fifth one read « the last ». Every day here is a
 * local day: the weekday of 2025-10-27 is the one of the calendar, never of UTC.
 */

// Monday first, as the days of the custom form.
export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
export const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'];
export const MAX_INTERVAL = 99;
export const MAX_COUNT = 730;
export const MAX_UNTIL_YEARS = 5;
const WORKING_DAYS = ['MO', 'TU', 'WE', 'TH', 'FR'];
// Date#getDay() order.
const BY_GET_DAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const LOCALES = { fr: 'fr-FR', en: 'en-US' };
const TEXTS = { fr: LangFr.data.recurrence, en: LangEn.data.recurrence };

const fill = (template, values = {}) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.split(`{${key}}`).join(String(value)),
    template ?? '',
  );

const langOf = (lang) => (lang === 'en' ? 'en' : 'fr');

// The code of the app's Language manager ('fr' unless English is current).
export const languageCode = (language) =>
  language?.current && language.current === language.languages?.get('en')
    ? 'en'
    : 'fr';

export const weekdayOf = (startDay) =>
  BY_GET_DAY[fromIsoDay(startDay).getDay()];

// The rank of the first day's weekday in its month: 1 to 4, 5 for the last.
export const nthOf = (startDay) =>
  Math.ceil(fromIsoDay(startDay).getDate() / 7);

// « lundi », « Monday », from a reference week starting on Monday 2024-01-01.
export const weekdayName = (code, lang) =>
  new Intl.DateTimeFormat(LOCALES[langOf(lang)], { weekday: 'long' }).format(
    new Date(2024, 0, 1 + WEEKDAYS.indexOf(code), 12),
  );

const sortWeekdays = (days) => WEEKDAYS.filter((code) => days.includes(code));

// The day `years` after `day`, both YYYY-MM-DD (29 February falls to 1 March).
export const addYears = (day, years) => {
  const [year, month, date] = day.split('-').map(Number);
  const next = new Date(year + years, month - 1, date, 12);
  const pad = (value) => String(value).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
};

// The rule in the api's shape: only the keys of its frequency.
export const normalizeRule = (rule, startDay) => {
  const base = { freq: rule.freq, interval: Number(rule.interval) || 1 };
  if (rule.freq === 'weekly') {
    const days = sortWeekdays(rule.byWeekday ?? []);
    base.byWeekday = days.length ? days : [weekdayOf(startDay)];
  }
  if (rule.freq === 'monthly')
    base.monthlyBy = rule.monthlyBy === 'weekday' ? 'weekday' : 'monthday';
  const end = rule.end ?? { type: 'never' };
  if (end.type === 'until') base.end = { type: 'until', day: end.day };
  else if (end.type === 'count')
    base.end = { type: 'count', count: Number(end.count) };
  else base.end = { type: 'never' };
  return base;
};

export const sameRule = (a, b) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * The choices of Google Calendar for a first day, each with its rule (null:
 * « Ne se répète pas »): [{ key, label, rule }]. « Personnaliser… » is the
 * field's, not a rule.
 */
export const presetsFor = (startDay, lang = 'fr') => {
  const never = { type: 'never' };
  const rules = [
    ['none', null],
    ['daily', { freq: 'daily', interval: 1, end: never }],
    [
      'weekly',
      {
        freq: 'weekly',
        interval: 1,
        byWeekday: [weekdayOf(startDay)],
        end: never,
      },
    ],
    [
      'monthly',
      { freq: 'monthly', interval: 1, monthlyBy: 'weekday', end: never },
    ],
    ['yearly', { freq: 'yearly', interval: 1, end: never }],
    [
      'weekdays',
      { freq: 'weekly', interval: 1, byWeekday: WORKING_DAYS, end: never },
    ],
  ];
  const texts = TEXTS[langOf(lang)];
  return rules.map(([key, rule]) => ({
    key,
    rule,
    label: rule ? describeRecurrence(rule, startDay, lang) : texts.none,
  }));
};

const listOf = (items, texts) =>
  items.length < 2
    ? items.join('')
    : fill(texts.listAnd, {
        list: items.slice(0, -1).join(', '),
        last: items[items.length - 1],
      });

// « le lundi et le mercredi », « on Monday and Wednesday ».
const weekdaysPhrase = (days, lang) => {
  const texts = TEXTS[lang];
  const items = days.map((code) =>
    fill(texts.dayItem, { day: weekdayName(code, lang) }),
  );
  return fill(texts.daysOn, { days: listOf(items, texts) });
};

// « le quatrième lundi », « on the last Monday ».
export const nthWeekdayPhrase = (startDay, lang = 'fr') => {
  const code = langOf(lang);
  return fill(TEXTS[code].nthWeekdayOn, {
    nth: TEXTS[code][`nth_${nthOf(startDay)}`],
    day: weekdayName(weekdayOf(startDay), code),
  });
};

const endPhrase = (end, lang) => {
  const texts = TEXTS[lang];
  if (end?.type === 'count') {
    const count = Number(end.count);
    const singular = lang === 'fr' ? count < 2 : count === 1;
    return fill(singular ? texts.countOne : texts.countOther, { count });
  }
  if (end?.type === 'until' && end.day) {
    const date = new Intl.DateTimeFormat(LOCALES[lang], {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(fromIsoDay(end.day));
    return fill(texts.untilSuffix, { date });
  }
  return '';
};

/**
 * A rule in words, as Google Calendar writes it: « Toutes les semaines le
 * lundi », « Tous les 2 jours », « Tous les mois le dernier vendredi, 10
 * fois », « …, jusqu’au 15 déc. 2026 ». `lang`: 'fr' or 'en'.
 */
export const describeRecurrence = (rule, startDay, lang = 'fr') => {
  const code = langOf(lang);
  const texts = TEXTS[code];
  if (!rule) return texts.none;
  const interval = Number(rule.interval) || 1;
  const many = interval > 1 ? 'Other' : 'One';
  let phrase;
  if (rule.freq === 'daily') {
    phrase = fill(texts[`daily${many}`], { count: interval });
  } else if (rule.freq === 'weekly') {
    const days = sortWeekdays(
      rule.byWeekday?.length ? rule.byWeekday : [weekdayOf(startDay)],
    );
    phrase =
      interval === 1 && sameRule(days, WORKING_DAYS)
        ? texts.weekdaysPreset
        : fill(texts[`weekly${many}`], {
            count: interval,
            days: weekdaysPhrase(days, code),
          });
  } else if (rule.freq === 'monthly') {
    const on =
      rule.monthlyBy === 'weekday'
        ? nthWeekdayPhrase(startDay, code)
        : fill(texts.monthDayOn, { day: fromIsoDay(startDay).getDate() });
    phrase = fill(texts[`monthly${many}`], { count: interval, on });
  } else {
    const date = new Intl.DateTimeFormat(LOCALES[code], {
      day: 'numeric',
      month: 'long',
    }).format(fromIsoDay(startDay));
    phrase = fill(texts[`yearly${many}`], {
      count: interval,
      on: fill(texts.yearDayOn, { date }),
    });
  }
  return `${phrase}${endPhrase(rule.end, code)}`;
};
